import { useState, useEffect, useMemo } from "react";
import { designerAccountValidation } from "shared/utils/validation";
import { useFormik } from "formik";
import { UpdateUserService, GetUserProfileService } from "shared/services/user";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { useSelector, useDispatch } from "react-redux";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./style.module.scss";
import ProfileUpload from "shared/components/common/customProfileUpload";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomInput from "shared/components/common/customInput";
import CustomAutoComplete from "shared/components/common/customAutoComplete";
import NotificationPreference from "shared/components/common/customNotificationPreference";
import ChangePasswordSection from "shared/components/common/changePassword";
import CustomButton from "shared/components/common/customButton";
import MobileLogoutButton from "shared/components/dashboardCommon/logoutButton";

const BuilderAccountSetting = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.root.auth);
  const user = useMemo(() => auth?.user || {}, [auth?.user]);

  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [deleteProfileAsset, setDeleteProfileAsset] = useState<boolean>(false);
  const [formData, setFormData] = useState({
    displayName: user.display_name || "",
    receiveEmailNotification: user.receive_email_notification || false,
    companyName: user.company_name || "",
    companyAddress: user.company_address || "",
    companyWebsite: user.company_website || "",
    businessCellNumber: user.company_phone_number || "",
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await GetUserProfileService();
        if (response.status === 200) {
          const profileData = response.data?.data || response.data;

          setFormData({
            displayName: profileData.display_name || user.display_name || "",
            receiveEmailNotification:
              profileData.receive_email_notifications ||
              profileData.receive_email_notification ||
              user.receive_email_notifications ||
              user.receive_email_notification ||
              false,
            companyName: profileData.company_name || "",
            companyAddress: profileData.company_address || "",
            companyWebsite: profileData.company_website || "",
            businessCellNumber: profileData.company_phone_number || "",
          });

          const profileImageUrl =
            profileData.profile_asset?.path ||
            profileData.path ||
            profileData.user_profile_asset?.path ||
            user.profile_asset?.path ||
            user.path ||
            user.user_profile_asset?.path;
          if (profileImageUrl) {
            setPreview(profileImageUrl);
          }
        }
      } catch (error: any) {
        console.error("Error fetching user profile:", error);
        if (user) {
          setFormData({
            displayName: user.display_name || "",
            receiveEmailNotification:
              user.receive_email_notifications ||
              user.receive_email_notification ||
              false,
            companyName: user.company_name || "",
            companyAddress: user.company_address || "",
            companyWebsite: user.company_website || "",
            businessCellNumber: user.company_phone_number || "",
          });
          const profileImageUrl =
            user.profile_asset?.path ||
            user.path ||
            user.user_profile_asset?.path;
          if (profileImageUrl) {
            setPreview(profileImageUrl);
          }
        }
      }
    };

    fetchUserProfile();
  }, [user]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: formData.displayName,
      companyName: formData.companyName,
      companyAddress: formData.companyAddress,
      companyWebsite: formData.companyWebsite,
      businessCellNumber: formData.businessCellNumber,
      receiveEmailNotification: formData.receiveEmailNotification,
    },
    validationSchema: designerAccountValidation,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let payload: any;
        if (profileFile || deleteProfileAsset) {
          payload = new FormData();
          payload.append("display_name", values.displayName);
          payload.append(
            "receive_email_notification",
            values.receiveEmailNotification.toString(),
          );
          payload.append("company_name", values.companyName);
          payload.append("company_address", values.companyAddress);
          payload.append("company_website", values.companyWebsite);
          payload.append("company_phone_number", values.businessCellNumber);

          if (profileFile) {
            payload.append("profile_asset", profileFile);
          }

          if (deleteProfileAsset) {
            payload.append("delete_profile_asset", "true");
          }
        } else {
          payload = {
            display_name: values.displayName,
            receive_email_notification: values.receiveEmailNotification,
            company_name: values.companyName,
            company_address: values.companyAddress,
            company_website: values.companyWebsite,
            company_phone_number: values.businessCellNumber,
          };
        }

        const response = await UpdateUserService(payload);

        if (response.status === 200) {
          const message =
            response.data?.message ||
            response.data?.data?.message ||
            "Profile updated successfully";
          toastMessage("success", message);
          setFormData(values);

          const hadProfileFile = !!profileFile;
          const hadDeleteRequest = deleteProfileAsset;

          const updatedUser = {
            ...user,
            display_name: values.displayName,
            receive_email_notification: values.receiveEmailNotification,
            company_name: values.companyName,
            company_address: values.companyAddress,
            company_website: values.companyWebsite,
            company_phone_number: values.businessCellNumber,
          };

          if (hadDeleteRequest) {
            updatedUser.profile_asset = null;
            updatedUser.path = null;
            setPreview(null);
          } else if (hadProfileFile) {
            const newProfileAssetPath =
              response.data?.profile_asset ||
              response.data?.profile_asset?.path ||
              response.data?.path ||
              response.data?.data?.profile_asset ||
              response.data?.data?.path;

            if (newProfileAssetPath) {
              updatedUser.profile_asset = {
                ...user.profile_asset,
                path: newProfileAssetPath,
              };
              updatedUser.path = newProfileAssetPath;
              setPreview(newProfileAssetPath);
            }
          }

          dispatch(setAuthReducer({ user: updatedUser }));

          setProfileFile(null);
          setDeleteProfileAsset(false);
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    isValid,
  } = formik;

  const handleRemoveProfilePicture = () => {
    setProfileFile(null);
    setPreview(null);
    setDeleteProfileAsset(true);
  };

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col md:gap-5 sm:gap-4 gap-3 w-full h-full",
      )}
    >
      <CustomPageHeading
        title="Profile & Settings"
        description="Tailor your creative setup"
      />

      <div className={classNames(styles.contentEnclosure, "grid")}>
        <ProfileUpload
          title={user.display_name || ""}
          description={
            user.user_type
              ? user.user_type
                  .split("_")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(" ")
              : ""
          }
          defaultPreview={preview}
          onChange={(file, url) => {
            setProfileFile(file);
            setPreview(url);
            setDeleteProfileAsset(false);
          }}
          onRemove={handleRemoveProfilePicture}
        />

        <form onSubmit={handleSubmit}>
          <div className={classNames(styles.alignmentEnclosure)}>
            <div className={classNames(styles.inputEnclosure, "grid")}>
              <CustomInput
                label="Display Name"
                required
                placeholder="Anderson Smith"
                name="displayName"
                value={values.displayName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.displayName && errors.displayName
                    ? errors.displayName
                    : ""
                }
                notRequiredMargin
                customInputContainer={styles.inputContainer}
              />
              <CustomInput
                label="Email"
                placeholder="markbob@gmail.com"
                name="email"
                value={user.email || ""}
                readOnly
                notRequiredMargin
                customInputContainer={styles.inputContainer}
              />
              <CustomInput
                label="Company Name"
                placeholder="e.g. Blu standard inc."
                name="companyName"
                value={values.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                notRequiredMargin
                customInputContainer={styles.inputContainer}
              />
              <CustomAutoComplete
                label="Company Address"
                placeholder="Enter street address"
                formikKey="companyAddress"
                value={values.companyAddress}
                setFieldValue={formik.setFieldValue}
                notRequiredMargin
                customInputContainer={styles.inputContainer}
              />
              <CustomInput
                label="Company Website"
                placeholder="Enter URL"
                name="companyWebsite"
                value={values.companyWebsite}
                onChange={handleChange}
                onBlur={handleBlur}
                error={
                  touched.companyWebsite && errors.companyWebsite
                    ? errors.companyWebsite
                    : ""
                }
              />
              <CustomInput
                label="Business & Cell Number"
                placeholder="+1 000 000 0000"
                name="businessCellNumber"
                value={values.businessCellNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                customInputContainer={styles.inputContainer}
              />
            </div>

            <div className={classNames(styles.buttonEnclosure)}>
              <CustomButton
                title="Save Changes"
                containerStyle={classNames(styles.submitButton)}
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || !isValid}
              />
            </div>
          </div>
        </form>
      </div>

      <ChangePasswordSection />
      <NotificationPreference
        icon={<Icons.EditState />}
        emailEnabled={values.receiveEmailNotification}
        onEmailToggleChange={(checked: boolean) => {
          formik.setFieldValue("receiveEmailNotification", checked);
        }}
      />

      <MobileLogoutButton />
    </div>
  );
};

export default BuilderAccountSetting;
